// AvailablePageDialog.js - Dialog for creating/editing available pages
import {
  useState,
  useEffect,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControlLabel,
  Checkbox,
  Stack,
  Alert,
  CircularProgress
} from './SettingsCommon';

const AvailablePageDialog = ({ open, data, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    page_id: '',
    label: '',
    path: '',
    active: true,
    sort_order: 0
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (data) {
      setFormData({
        page_id: data.page_id || data.id || '',
        label: data.label || '',
        path: data.path || '',
        active: data.active ?? true,
        sort_order: data.sort_order ?? 0
      });
    } else {
      setFormData({
        page_id: '',
        label: '',
        path: '',
        active: true,
        sort_order: 0
      });
    }
    setError(null);
  }, [data, open]);

  const handleSubmit = async () => {
    if (!formData.page_id.trim() || !formData.label.trim() || !formData.path.trim()) {
      setError('Page ID, Label, and Path are required');
      return;
    }

    setSaving(true);
    setError(null);

    try {
      await onSave(formData);
    } catch (err) {
      setError(err.message || 'Failed to save page');
    } finally {
      setSaving(false);
    }
  };

  const isEdit = !!data?.page_id || !!data?.id;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEdit ? 'Edit Page' : 'Add New Page'}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
        <Stack spacing={2} sx={{ mt: 1 }}>
          <TextField
            label="Page ID (internal)"
            value={formData.page_id}
            onChange={(e) => setFormData({ ...formData, page_id: e.target.value.toLowerCase().replace(/\s+/g, '_') })}
            fullWidth
            required
            placeholder="e.g., reports"
            helperText="Lowercase, no spaces (use underscores)"
            disabled={isEdit}
          />
          <TextField
            label="Display Label"
            value={formData.label}
            onChange={(e) => setFormData({ ...formData, label: e.target.value })}
            fullWidth
            required
            placeholder="e.g., Reports"
          />
          <TextField
            label="Route Path"
            value={formData.path}
            onChange={(e) => setFormData({ ...formData, path: e.target.value })}
            fullWidth
            required
            placeholder="e.g., /Reports"
            helperText="Must match the route in App.js"
          />
          <TextField
            label="Sort Order"
            type="number"
            value={formData.sort_order}
            onChange={(e) => setFormData({ ...formData, sort_order: parseInt(e.target.value) || 0 })}
            fullWidth
            placeholder="0"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
              />
            }
            label="Active"
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>Cancel</Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={saving}
          startIcon={saving ? <CircularProgress size={16} /> : null}
        >
          {isEdit ? 'Save Changes' : 'Add Page'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AvailablePageDialog;
